import { useState, useCallback, useEffect } from 'react';
import { listThreads as listThreadsApi, getErrorMessage, type ThreadWithReplies } from '@/lib/api';

interface UseThreadsReturn {
  threads: ThreadWithReplies[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching threads for a project
 */
export const useThreads = (projectId: string | undefined): UseThreadsReturn => {
  const [threads, setThreads] = useState<ThreadWithReplies[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreads = useCallback(async () => {
    if (!projectId) {
      setThreads([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await listThreadsApi({
        projectId,
        includeReplies: true,
      });
      // The API returns threads with replies when includeReplies is true
      setThreads(response.threads as ThreadWithReplies[]);
    } catch (err) {
      console.error('Failed to fetch threads:', err);
      setError(getErrorMessage(err));
      setThreads([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  return {
    threads,
    isLoading,
    error,
    refetch: fetchThreads,
  };
};

export default useThreads;
